import {
	BulkDeleteButton,
	DateField,
	ReferenceField,
	Show,
	TabbedShowLayout,
	TextField,
	RichTextField,
	List,
	DataTable,
	ReferenceInput,
	AutocompleteInput,
	useRecordContext,
	useGetOne
} from 'react-admin';
import jsonExport from 'jsonexport/dist';

import { Button, Chip, Stack } from '@mui/material';
import icon from '@mui/icons-material/Forum';
import { normalizeFilename, saveAs } from '../../util';

/**
 * Custom exporter for Interview list - exports only essential fields,
 * excluding lengthy transcript and summary content.
 */
const interviewExporter = async (records, fetchRelatedRecords) => {
	// Fetch session names for all interviews
	const sessions = await fetchRelatedRecords(records, 'session_id', 'sessions');

	const exportData = records.map(record => ({
		'Session': sessions[record.session_id]?.name ?? '',
		'Name': record.name ?? '',
		'Company': record.company ?? '',
		'Role': record.role ?? '',
		'Created': record.createdAt ? new Date(record.createdAt).toLocaleString() : ''
	}));

	jsonExport(exportData, { rowDelimiter: ',' }, (err, csv) => {
		if (err) {
			console.error('Export error:', err);
			return;
		}
		saveAs(csv, 'interviews.csv', 'text/csv');
	});
};

const show = () => (
	<Show>
		<TabbedShowLayout>
			<TabbedShowLayout.Tab label="Details">
				<ReferenceField source="session_id" reference="sessions" />
				<TextField source="name" />
				<TextField source="company" />
				<TextField source="role" />
				<DateField source="createdAt" showDate showTime />
				<DateField source="updatedAt" showDate showTime />
			</TabbedShowLayout.Tab>
			<TabbedShowLayout.Tab label="Transcript">
				<RichTextField source="transcript_formatted" label="Transcript" />
			</TabbedShowLayout.Tab>
			<TabbedShowLayout.Tab label="Feedback">
				<RichTextField source="summary_formatted" label="Feedback" />
			</TabbedShowLayout.Tab>
		</TabbedShowLayout>
	</Show>
);

const eventFilterQuery = searchText => ({ name: `%${searchText}%` });
const groupFilterQuery = searchText => ({ name: `%${searchText}%` });

const formFilters = [
	<ReferenceInput label="Group" source="group_id" reference="groups" alwaysOn>
		<AutocompleteInput source="name" filterToQuery={groupFilterQuery} />
	</ReferenceInput>,
	<ReferenceInput label="Events" source="session_id" reference="sessions" alwaysOn>
		<AutocompleteInput source="name" filterToQuery={eventFilterQuery} />
	</ReferenceInput>
];

const ExportInterviewButton = () => {
	const record = useRecordContext();
	const { data: session, isLoading } = useGetOne( 'sessions', { id: record?.session_id }, { enabled: !!record?.session_id } );
	const { data: module, isLoading: moduleLoading } = useGetOne( 'modules', { id: record?.module_id }, { enabled: !!record?.module_id } );

	const handleExport = (event) => {
		event.stopPropagation();
		const session_name = session?.name ?? '(Unknown Session)';
		const module_name = module ? module.title : '(Unknown Module)';

		const filename = normalizeFilename( session_name + '-' + record.id ) + '.html';
		const meetingDate = new Date( record.createdAt );
		const timestamp = meetingDate.toLocaleDateString() + ' ' + meetingDate.toLocaleTimeString();

		let content = `
		<h1>${record.name} - ${record.role}</h1>
		<h2>Session: ${session_name}</h2>
		<h2>Module: ${module_name}</h2>
		<p>${timestamp}</p>
		

		<h2>Interview</h2>
		<blockquote>${record.transcript_formatted}</blockquote>

		<h2>Feedback</h2>
		<blockquote>${record.summary_formatted}</blockquote>
		`;
		saveAs( content, filename, 'text/html' );
	};

	return <Button onClick={handleExport} disabled={isLoading || moduleLoading}>Export</Button>
}

const SessionGroupsField = () => {
	const record = useRecordContext();
	const { data: session, isLoading } = useGetOne(
		'sessions',
		{ id: record?.session_id },
		{ enabled: !!record?.session_id }
	);

	if (isLoading || !session?.groups) {
		return null;
	}

	return (
		<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
			{session.groups.map(group => (
				<Chip key={group.id} label={group.name} size="small" />
			))}
		</Stack>
	);
};

const list = () => (
	<List
		exporter={interviewExporter}
		filters={formFilters}
		>
		<DataTable bulkActionButtons={<BulkDeleteButton mutationMode="pessimistic" />}>
			<DataTable.Col label="Session">
				<ReferenceField source="session_id" reference="sessions" link={false} />
			</DataTable.Col>
			<DataTable.Col label="Groups">
				<SessionGroupsField />
			</DataTable.Col>
			<DataTable.Col source="name" />
			<DataTable.Col source="company" />
			<DataTable.Col source="role" />
			<DataTable.Col label="Created">
				<DateField source="createdAt" showDate showTime />
			</DataTable.Col>
			<DataTable.Col label="Updated">
				<DateField source="updatedAt" showDate showTime />
			</DataTable.Col>
			<DataTable.Col>
				<ExportInterviewButton />
			</DataTable.Col>
		</DataTable>
	</List>
);


export const Interview = {
	show,
	list,
	icon
};

export default Interview;
